import { useSplitClient } from '@splitsoftware/splitio-react';
import { getRealmName } from 'src/routes/AuthContext';
import { useFacilityId } from 'src/hooks/use-facility-id';

type Treatment = string;
type Source = 'FACILITY' | 'ENTERPRISE';

/**
 * Resolve the single Split key a toggle needs.
 *
 * These hooks used to call `useSplitClient` three times each (context + facility + enterprise)
 * regardless of `source`, so an enterprise-only screen still instantiated the facility client —
 * and every shared client costs its own `/mySegments` fetch plus a streaming channel.
 *
 * The ENTERPRISE key matches the factory's own key (see `sdkConfig` in `app.tsx`), so it resolves
 * to the main client rather than allocating a shared one.
 *
 * `facilityId` comes from the reactive store rather than a bare localStorage read, so switching
 * facility re-keys the client immediately instead of waiting for an unrelated re-render.
 *
 * Note: an empty key is falsy, so `useSplitClient` falls back to the factory's realm-keyed client.
 * Physicians have no facility, so their FACILITY-source toggles resolve against the realm. That is
 * long-standing behaviour, preserved here deliberately.
 */
function useSplitKey(source: Source): string {
  const facilityId = useFacilityId();
  const enterpriseKey = getRealmName()?.toLowerCase() || '';

  return source === 'FACILITY' ? facilityId : enterpriseKey;
}

function useFeatureToggle(treatment: Treatment, source: Source) {
  const splitKey = useSplitKey(source);
  const { client } = useSplitClient({ splitKey });

  return [client?.getTreatment(treatment) === 'on'];
}

export function useFeatureToggleWhenReady(treatment: Treatment, source: Source) {
  const splitKey = useSplitKey(source);
  const { client, isReady } = useSplitClient({ splitKey });

  if (!isReady) return [null];

  return [client?.getTreatment(treatment) === 'on'];
}

export default useFeatureToggle;
